/**
 * Normalizer for the `GET /agencies/alltypes` response.
 *
 * The endpoint returns flat rows — one per partner × agency combination.
 * This module groups them into the canonical Agency shape:
 *
 *   [{
 *     agencies_id:         'vta',
 *     agencies_short_name: 'VTA',
 *     agencies_long_name:  'VTA',
 *     agency_type:         'transit',
 *     jurisdictions: [{
 *       id:       'sjc',
 *       name:     'San Jose',
 *       center:   { lat: 37.35, lng: -121.89 },
 *       timezone: 'America/Los_Angeles',
 *       region:   { abbreviation: 'sfbay', name: 'SF Bay Area', center_lat: 37.64, center_lng: -122.09 } | null,
 *     }],
 *   }]
 */

/**
 * Normalise the flat rows from `GET /agencies/alltypes` into the sidebar-
 * compatible agency list grouped by `agency_abbreviation`.
 *
 * @param {Array<Object>} rows — flat rows from the API
 * @returns {Array<Object>} — array of agency objects with nested jurisdictions
 */
export const normalizeAllAgencyTypes = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    // Group rows by agency_abbreviation
    const agencyMap = new Map();

    rows.forEach((row) => {
        const {
            agency_abbreviation,
            agency_name,
            agency_type,
            partner_abbreviation,
            partner_name,
            center_latitude,
            center_longitude,
            timezone,
            region_abbreviation,
            region_name,
            region_center_latitude,
            region_center_longitude,
        } = row;

        if (!agency_abbreviation) return;

        if (!agencyMap.has(agency_abbreviation)) {
            agencyMap.set(agency_abbreviation, {
                agencies_id: agency_abbreviation,
                agencies_short_name: agency_name || agency_abbreviation,
                agencies_long_name: agency_name || agency_abbreviation,
                agency_type: agency_type || 'transit',
                jurisdictions: [],
                // Track seen partner abbreviations to avoid duplicates
                _seenPartners: new Set(),
            });
        }

        const agency = agencyMap.get(agency_abbreviation);

        // Add the partner/city if we haven't seen it yet for this agency
        if (partner_abbreviation && !agency._seenPartners.has(partner_abbreviation)) {
            agency._seenPartners.add(partner_abbreviation);

            const jurisdiction = {
                id: partner_abbreviation,
                name: partner_name || partner_abbreviation,
                center: {
                    lat: parseFloat(center_latitude) || 0,
                    lng: parseFloat(center_longitude) || 0,
                },
                timezone: timezone || null,
            };

            // Attach region info if present
            if (region_abbreviation) {
                jurisdiction.region = {
                    abbreviation: region_abbreviation,
                    name: region_name || region_abbreviation,
                    center_lat: parseFloat(region_center_latitude) || null,
                    center_lng: parseFloat(region_center_longitude) || null,
                };
            } else {
                jurisdiction.region = null;
            }

            agency.jurisdictions.push(jurisdiction);
        }
    });

    // Convert the map to an array and strip the internal helper set
    return Array.from(agencyMap.values()).map(({ _seenPartners, ...agency }) => agency);
};
